import { useState, useEffect, useRef } from "react";
import ResetPasswordModal from "../components/ResetPasswordModal";
import { fetchBranchList, fetchDivisionList } from "../api/branchApi";
import { QRCodeCanvas } from "qrcode.react";
import logoImg from '../assets/logo.png';

export default function Login({ onLoginSuccess }) {
  const [empNo,      setEmpNo]      = useState("");
  const [password,   setPassword]   = useState("");
  const [loading,    setLoading]    = useState(false);
  const [error,      setError]      = useState("");
  const [showPw,     setShowPw]     = useState(false);
  const [showResetPw, setShowResetPw] = useState(false);

  const [branchList,        setBranchList]        = useState([]);
  const [divisionList,      setDivisionList]      = useState([]);
  const [branchCode,        setBranchCode]        = useState("");
  const [divisionCode,      setDivisionCode]      = useState("");
  const [savedDivisionCode, setSavedDivisionCode] = useState("");
  const [divisionPhone, setDivisionPhone] = useState('');
  const [showQrModal, setShowQrModal] = useState(false);
  const qrCanvasRef = useRef(null);

  const BASE_URL = process.env.REACT_APP_API_URL || '/api';

  // 1. 지부 목록 로드 + URL 파라미터 / localStorage 자동 세팅
  useEffect(() => {
    const init = async () => {
      const res = await fetchBranchList();
      if (res.success) setBranchList(res.data);

      const params      = new URLSearchParams(window.location.search);
      const urlBranch   = params.get('branch');
      const urlDivision = params.get('division');

      const savedBranch   = urlBranch   || localStorage.getItem('branch_code')   || '';
      const savedDivision = urlDivision || localStorage.getItem('division_code') || '';

      if (savedBranch)   setBranchCode(savedBranch);
      if (savedDivision) setSavedDivisionCode(savedDivision);
    };
    init();
  }, []);

  // 2. 지부 변경 시 분회 목록 로드
  useEffect(() => {
    if (!branchCode) { setDivisionList([]); setDivisionCode(''); return; }
    const load = async () => {
      const res = await fetchDivisionList(branchCode);
      if (res.success) {
        setDivisionList(res.data);
      }
    };
    load();
  }, [branchCode]);

  // 3. 분회 목록 로드 완료 후 savedDivisionCode 세팅
  useEffect(() => {
    if (savedDivisionCode && divisionList.length > 0) {
      setDivisionCode(savedDivisionCode);
      // ↓ phone도 함께 세팅
      const found = divisionList.find(d => d.division_code === savedDivisionCode);
      setDivisionPhone(found?.phone || '');
      setSavedDivisionCode('');
    }
  }, [divisionList, savedDivisionCode]);

  const handleBranchChange = (e) => {
    setBranchCode(e.target.value);
    setDivisionCode('');
  };

  const handleLogin = async () => {
    if (!branchCode || !divisionCode) {
      setError("지부와 분회를 선택해주세요.");
      return;
    }
    if (!empNo || !password) {
      setError("사번과 비밀번호를 입력해주세요.");
      return;
    }
    setLoading(true);
    setError("");

    try {
      const res = await fetch(`${BASE_URL}/auth/login`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          emp_no: empNo,
          password,
          branch_code: branchCode,
          division_code: divisionCode
        }),
      });
      const data = await res.json();

      if (data.success) {
        localStorage.setItem('token',         data.accessToken);
        localStorage.setItem('refreshToken',  data.refreshToken);
        localStorage.setItem('user',          JSON.stringify(data.user));
        localStorage.setItem('userLevel',     data.user.user_level);
        localStorage.setItem('branch_code',   branchCode);
        localStorage.setItem('division_code', divisionCode);
        onLoginSuccess();
      } else {
        setError(data.message);
      }
    } catch (err) {
      setError("서버 연결에 실패했습니다.");
    } finally {
      setLoading(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter") handleLogin();
  };

  // 비밀번호 찾기
  const handleResetPwClick = () => {
    if (!empNo) {
      setError("비밀번호 찾기를 위해 사번을 먼저 입력해주세요.");
      return;
    }
    setShowResetPw(true);
  };

  // 링크 생성
  const getLoginLink = () => `${window.location.origin}/?branch=${branchCode}&division=${divisionCode}`;

  // 링크 복사 폴백
  const fallbackCopy = (text) => {
    const textarea = document.createElement('textarea');
    textarea.value = text;
    textarea.style.position = 'fixed';
    textarea.style.opacity = '0';
    document.body.appendChild(textarea);
    textarea.focus();
    textarea.select();
    try {
      document.execCommand('copy');
      alert('✅ 링크가 복사되었습니다!');
    } catch {
      alert('❌ 복사에 실패했습니다.');
    }
    document.body.removeChild(textarea);
  };

  // 링크 복사
  const handleCopyLink = () => {
    if (!branchCode || !divisionCode) {
      setError("지부와 분회를 먼저 선택해주세요.");
      return;
    }
    const url = getLoginLink();
    if (navigator.clipboard && window.isSecureContext) {
      navigator.clipboard.writeText(url)
        .then(() => alert('✅ 링크가 복사되었습니다!'))
        .catch(() => fallbackCopy(url));
    } else {
      fallbackCopy(url);
    }
  };

  // QR생성
  const handleShowQr = () => {
    if (!branchCode || !divisionCode) {
      setError("지부와 분회를 먼저 선택해주세요.");
      return;
    }
    setShowQrModal(true);
  };

  // QR저장
  const handleDownloadQr = () => {
    const canvas = qrCanvasRef.current?.querySelector('canvas');
    if (!canvas) return;
    const url = canvas.toDataURL('image/png');
    const a = document.createElement('a');
    a.href = url;
    a.download = `로그인QR_${branchCode}_${divisionCode}.png`;
    document.body.appendChild(a);
    a.click();
    a.remove();
  };

  return (
    <div style={styles.wrap}>
      <div style={styles.bgCircle1} />
      <div style={styles.bgCircle2} />
      <div style={styles.bgCircle3} />

      <div style={styles.card}>
        {/* 로고 */}
        <div style={styles.logoArea}>
          <img src={logoImg} alt="민주노총 공공운수노조 의료연대본부" style={{ height: 48, width: 'auto', objectFit: 'contain' }} />
          <span style={styles.logoSub}> 조합원 관리 시스템</span>
        </div>

        <div style={styles.divider} />

        <div style={styles.formArea}>
          <div style={styles.titleRow}>
            <h2 style={styles.title}>로그인</h2>
            <p style={styles.desc}>지부 · 분회를 선택하고 로그인해주세요.</p>
          </div>

          {/* 지부 선택 */}
          <div style={styles.inputGroup}>
            <label style={styles.label}>지부</label>
            <div style={styles.inputWrap}>
              <span style={styles.inputIcon}>🏢</span>
              <select style={styles.select} value={branchCode} onChange={handleBranchChange}>
                <option value="">지부를 선택하세요</option>
                {branchList.map(b => (
                  <option key={b.branch_code} value={b.branch_code}>{b.branch_name}</option>
                ))}
              </select>
            </div>
          </div>

          {/* 분회 선택 */}
          <div style={styles.inputGroup}>
            <label style={styles.label}>분회</label>
            <div style={styles.inputWrap}>
              <span style={styles.inputIcon}>🏥</span>
              <select
                style={{ ...styles.select, background: !branchCode ? "#f0f0f0" : "#fafafa", color: !branchCode ? "#aaa" : "#1a1a2e" }}
                value={divisionCode}
                  onChange={e => {
                    setDivisionCode(e.target.value);
                    const found = divisionList.find(d => d.division_code === e.target.value);
                    setDivisionPhone(found?.phone || '');
                  }}
                disabled={!branchCode}
              >
                <option value="">분회를 선택하세요</option>
                {divisionList.map(d => (
                  <option key={d.division_code} value={d.division_code}>{d.division_name}</option>
                ))}
              </select>
            </div>
          </div>

          {/* 사번 */}
          <div style={styles.inputGroup}>
            <label style={styles.label}>사번</label>
            <div style={styles.inputWrap}>
              <span style={styles.inputIcon}>👤</span>
              <input
                style={styles.input}
                type="text"
                placeholder="사번을 입력하세요"
                value={empNo}
                onChange={e => setEmpNo(e.target.value)}
                onKeyDown={handleKeyDown}
                autoFocus
              />
            </div>
          </div>

          {/* 비밀번호 */}
          <div style={styles.inputGroup}>
            <label style={styles.label}>비번</label>
            <div style={styles.inputWrap}>
              <span style={styles.inputIcon}>🔒</span>
              <input
                style={styles.input}
                type={showPw ? "text" : "password"}
                placeholder="비밀번호를 입력하세요"
                value={password}
                onChange={e => setPassword(e.target.value)}
                onKeyDown={handleKeyDown}
              />
              <span style={styles.eyeIcon} onClick={() => setShowPw(!showPw)}>
                {showPw ? "🙈" : "👁"}
              </span>
            </div>
          </div>

          {error && (
            <div style={styles.errorBox}>
              ⚠️ {error}
              {divisionPhone && (
                <div style={{ marginTop: 6, fontSize: 12, color: "#888" }}>
                  문의: {divisionPhone}
                </div>
              )}
            </div>
          )}

          <button
            style={{ ...styles.loginBtn, opacity: loading ? 0.7 : 1, cursor: loading ? "not-allowed" : "pointer" }}
            onClick={handleLogin}
            disabled={loading}
          >
            {loading ? "로그인 중..." : "로그인"}
          </button>

          <button
            style={{ ...styles.loginBtn, background: "#fff", border: "1.5px solid #1677ff", color: "#1677ff", marginTop: 8 }}
            onClick={() => window.location.href = `/?page=join${branchCode ? `&branch=${branchCode}` : ''}${divisionCode ? `&division=${divisionCode}` : ''}`}>
            ✊ 조합원 가입신청
          </button>

          <div style={{ textAlign: "center", marginTop: 8 }}>
            <span
              style={{ fontSize: 13, color: "#888", cursor: "pointer", textDecoration: "underline" }}
              onClick={handleResetPwClick}
            >
              비밀번호를 잊으셨나요?
            </span>
            {/* 링크복사 / QR 뱃지 */}
            <div style={{ display: "flex", justifyContent: "center", gap: 8, marginTop: 12 }}>
              <span
                style={{
                  display: "flex", alignItems: "center", gap: 4,
                  fontSize: 12, fontWeight: 600, padding: "6px 12px", borderRadius: 20,
                  background: branchCode && divisionCode ? "#f0f7ff" : "#f5f5f5",
                  color: branchCode && divisionCode ? "#1677ff" : "#bbb",
                  border: `1px solid ${branchCode && divisionCode ? "#d6e8ff" : "#eee"}`,
                  cursor: branchCode && divisionCode ? "pointer" : "not-allowed",
                  userSelect: "none",
                }}
                onClick={handleCopyLink}
              >
                🔗 링크복사
              </span>
              <span
                style={{
                  display: "flex", alignItems: "center", gap: 4,
                  fontSize: 12, fontWeight: 600, padding: "6px 12px", borderRadius: 20,
                  background: branchCode && divisionCode ? "#f0f7ff" : "#f5f5f5",
                  color: branchCode && divisionCode ? "#1677ff" : "#bbb",
                  border: `1px solid ${branchCode && divisionCode ? "#d6e8ff" : "#eee"}`,
                  cursor: branchCode && divisionCode ? "pointer" : "not-allowed",
                  userSelect: "none",
                }}
                onClick={handleShowQr}
              >
                🔳 QR코드
              </span>
            </div>
          </div>
          {showResetPw && (
            <ResetPasswordModal
              empNo={empNo}
              branchCode={branchCode}
              divisionCode={divisionCode}
              onClose={() => setShowResetPw(false)}
              onSuccess={() => {
                setShowResetPw(false);
                setError("비밀번호가 초기화되었습니다. 새 비밀번호로 로그인해주세요.");
              }}
            />
          )}
          {showQrModal && (
          <div style={{
            position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)",
            display: "flex", alignItems: "center", justifyContent: "center", zIndex: 2000
          }}>
            <div style={{ background: "#fff", borderRadius: 16, padding: 28, width: 320, textAlign: "center" }}>
              <div style={{ fontWeight: 800, fontSize: 16, marginBottom: 4, color: "#1a1a2e" }}>
                로그인 QR코드
              </div>
              <div style={{ fontSize: 12, color: "#888", marginBottom: 20 }}>
                {branchList.find(b => b.branch_code === branchCode)?.branch_name} · {divisionList.find(d => d.division_code === divisionCode)?.division_name}
              </div>
              <div ref={qrCanvasRef} style={{ display: "flex", justifyContent: "center", marginBottom: 20 }}>
                <QRCodeCanvas value={getLoginLink()} size={200} />
              </div>
              <div style={{ display: "flex", gap: 8 }}>
                <button
                  style={{ flex: 1, padding: "10px 0", borderRadius: 10, border: "1px solid #e0e0e0", background: "#fff", color: "#333", fontSize: 13, fontWeight: 600, cursor: "pointer" }}
                  onClick={() => setShowQrModal(false)}
                >닫기</button>
                <button
                  style={{ flex: 1, padding: "10px 0", borderRadius: 10, border: "none", background: "linear-gradient(135deg, #1677ff, #4096ff)", color: "#fff", fontSize: 13, fontWeight: 700, cursor: "pointer" }}
                  onClick={handleDownloadQr}
                >💾 저장</button>
              </div>
            </div>
          </div>
        )}
        </div>
        <div style={styles.footer}>
          {divisionPhone && (
            <div style={{ fontSize: 13, color: "#bbb", marginTop: 4 }}>
              📞 로그인 문의 : {divisionPhone}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

const styles = {
  wrap        : { minHeight: "100vh", background: "linear-gradient(135deg, #0f2027, #203a43, #2c5364)", display: "flex", alignItems: "center", justifyContent: "center", fontFamily: "'Noto Sans KR', sans-serif", position: "relative", overflow: "hidden" },
  bgCircle1   : { position: "absolute", width: 400, height: 400, borderRadius: "50%", background: "rgba(255,80,80,0.08)", top: -100, left: -100 },
  bgCircle2   : { position: "absolute", width: 300, height: 300, borderRadius: "50%", background: "rgba(255,255,255,0.04)", bottom: -50, right: -50 },
  bgCircle3   : { position: "absolute", width: 200, height: 200, borderRadius: "50%", background: "rgba(255,80,80,0.05)", top: "50%", right: "20%" },
  card        : { background: "rgba(255,255,255,0.97)", borderRadius: 24, padding: "40px 44px", width: "100%", maxWidth: 460, boxShadow: "0 25px 60px rgba(0,0,0,0.4)", position: "relative", zIndex: 10 },
  logoArea    : { display: "flex", alignItems: "baseline", gap: 10, marginBottom: 20 },
  logoIcon    : { fontSize: 36, lineHeight: 1 },
  logoMain    : { fontSize: 18, fontWeight: 800, color: "#c0392b", letterSpacing: "-0.5px" },
  logoSub     : { fontSize: 12, color: "#888" },
  divider     : { height: 1, background: "linear-gradient(to right, #c0392b33, #c0392b, #c0392b33)", marginBottom: 28 },
  formArea    : { display: "flex", flexDirection: "column", gap: 0 },
  titleRow    : { display: "flex", alignItems: "baseline", gap: 10, marginBottom: 24 },
  title       : { fontSize: 22, fontWeight: 800, color: "#1a1a2e", margin: 0 },
  desc        : { fontSize: 13, color: "#999", margin: 0 },
  inputGroup  : { display: "flex", alignItems: "center", gap: 14, marginBottom: 16 },
  label       : { fontSize: 13, fontWeight: 600, color: "#444", width: 24, flexShrink: 0 },
  inputWrap   : { position: "relative", display: "flex", alignItems: "center", flex: 1 },
  inputIcon   : { position: "absolute", left: 12, fontSize: 16, zIndex: 1 },
  input       : { width: "100%", padding: "12px 40px 12px 40px", borderRadius: 10, border: "1.5px solid #e0e0e0", fontSize: 14, outline: "none", background: "#fafafa", boxSizing: "border-box", color: "#1a1a2e" },
  select      : { width: "100%", padding: "12px 40px 12px 40px", borderRadius: 10, border: "1.5px solid #e0e0e0", fontSize: 14, outline: "none", background: "#fafafa", boxSizing: "border-box", color: "#1a1a2e", cursor: "pointer", appearance: "none" },
  eyeIcon     : { position: "absolute", right: 12, fontSize: 16, cursor: "pointer", userSelect: "none" },
  errorBox    : { background: "#fff5f5", border: "1px solid #ffcccc", borderRadius: 8, padding: "10px 14px", fontSize: 13, color: "#c0392b", marginBottom: 12 },
  loginBtn    : { width: "100%", padding: "14px", borderRadius: 10, border: "none", background: "linear-gradient(135deg, #c0392b, #e74c3c)", color: "#fff", fontSize: 15, fontWeight: 700, marginTop: 8, boxShadow: "0 4px 15px rgba(192,57,43,0.4)", letterSpacing: "0.5px" },
  footer      : { marginTop: 28, textAlign: "center" },
  footerText  : { fontSize: 11, color: "#bbb" },
};